import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useTranslation, Trans } from "react-i18next";
import { COLLECTIONS, whatsappUrl } from "@/lib/brand";

const Card = ({ itemKey, image, className, index }) => {
  const { t } = useTranslation();
  const title = t(`collections.items.${itemKey}.title`);
  const range = t(`collections.items.${itemKey}.range`);
  const desc = t(`collections.items.${itemKey}.desc`);
  const message = t("collections.whatsappMessage", { title });

  return (
    <motion.a
      href={whatsappUrl(message)}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`collection-card-${itemKey}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className={`group relative overflow-hidden rounded-sm border border-[#E5E5E0] ${className}`}
    >
      <img
        src={image}
        alt={title}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent" />
      <div className="relative h-full flex flex-col justify-end p-6 md:p-7">
        <span className="font-body text-xs uppercase tracking-[0.2em] text-[#F4B183] mb-2">
          {range}
        </span>
        <h3 className="font-display font-bold text-2xl md:text-3xl text-white flex items-center gap-2">
          {title}
          <ArrowUpRight
            className="opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all"
            size={22}
          />
        </h3>
        <p className="font-body text-sm text-white/80 mt-2 max-w-xs">{desc}</p>
      </div>
    </motion.a>
  );
};

export const Collections = () => {
  const { t } = useTranslation();
  const byKey = (k) => COLLECTIONS.find((c) => c.key === k);

  const infantil = byKey("infantil");
  const adulto = byKey("adulto");
  const plus = byKey("plus");

  return (
    <section
      id="colecoes"
      data-testid="collections-section"
      className="py-24 md:py-32 max-w-7xl mx-auto px-6 lg:px-10"
    >
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
        <div>
          <span className="font-body uppercase tracking-[0.28em] text-xs text-[#C86A3D] font-semibold">
            {t("collections.eyebrow")}
          </span>
          <h2 className="font-display font-black tracking-tight text-4xl md:text-5xl text-[#1A1A1A] mt-4 max-w-xl leading-tight">
            <Trans
              i18nKey="collections.title"
              components={{
                em: (
                  <span className="font-serif-editorial italic font-normal text-[#2A3563]" />
                ),
              }}
            />
          </h2>
        </div>
        <p className="font-body text-[#1A1A1A]/60 max-w-sm">
          {t("collections.description")}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 auto-rows-[280px]">
        {adulto && (
          <Card
            itemKey={adulto.key}
            image={adulto.image}
            index={0}
            className="md:col-span-7 md:row-span-2"
          />
        )}
        {infantil && (
          <Card
            itemKey={infantil.key}
            image={infantil.image}
            index={1}
            className="md:col-span-5"
          />
        )}
        {plus && (
          <Card
            itemKey={plus.key}
            image={plus.image}
            index={2}
            className="md:col-span-5"
          />
        )}
      </div>
    </section>
  );
};
